import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import WarningBox, { WarningBoxColorType } from 'src/components/ui/WarningBox/WarningBox';
import Divider from 'src/components/ui/Divider/Divider';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { ActionBar, InboundConfiguratorForm, Section } from './InboundConfiguratorForm';
import InboundBackordersCard from './InboundBackordersCard';
import { DownloadSection, InboundCreateShell } from './InboundCreateData';
import { inboundOrderToFormValues, useInboundFormContext } from './InboundFormContext';
import { getInboundDeliverySlip, useInboundOrderQuery, useInboundOrdersQuery } from '../../services/inbound';
import { Status } from '../../utils/general';
import { getInboundNavIds } from '../../utils/inboundNav';
import OverlayNavArrows from '../../components/layouts/OverlayNavArrows/OverlayNavArrows';
import { OverlayNavProvider } from '../../providers/OverlayNavProvider';

const InboundCreateConfirm: React.FC<{}> = () => {
  const { id: idString } = useParams();
  const id = Number.parseInt(idString ?? '', 10);

  if (!idString || Number.isNaN(id)) {
    throw new Error('id is required');
  }
  const { t } = useTranslation();
  const [paginationState, setPaginationState] = useState({
    currentPage: 1,
    itemsPerPage: 10,
  });

  const { currentPage, itemsPerPage } = paginationState;

  const offset = React.useMemo(() => {
    return (currentPage - 1) * itemsPerPage;
  }, [currentPage, itemsPerPage]);

  const limit = React.useMemo(() => itemsPerPage, [itemsPerPage]);

  const params = React.useMemo(() => {
    return {
      offset,
      limit,
    };
  }, [offset, limit]);

  const { data: inboundOrder, isLoading } = useInboundOrderQuery({
    path: { inbound_order_id: id },
    query: params,
  });

  // Ids for the prev/next arrow navigation. Primary source: the shared store the
  // inbound list fills on load (respects the user's filter/sort). Fallback for a
  // hard refresh / deep-link of this page (where the list never mounted): fetch
  // the default inbound list so the arrows still work.
  const storeNavIds = getInboundNavIds();
  const { data: inboundListFallback } = useInboundOrdersQuery(
    { page: 1, size: 200 },
    { enabled: storeNavIds.length === 0 },
  );
  const navIds = storeNavIds.length
    ? storeNavIds
    : (inboundListFallback?.items ?? []).map(o => o.id).filter((x): x is number => x != null);

  const handlePaginationChange = (page: number, perPage: number) => {
    const maxPages = inboundOrder?.total_pages ?? Infinity;
    const clampedPage = Math.max(1, Math.min(page, maxPages));
    setPaginationState({ currentPage: clampedPage, itemsPerPage: perPage });
  };

  const { setFormState } = useInboundFormContext();
  const [downloadLoading, setDownloadLoading] = useState(false);
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const success = queryParams.get('success') === '1';

  useEffect(() => {
    if (inboundOrder) {
      const transformed = inboundOrderToFormValues(inboundOrder);
      setFormState(transformed);
    }
  }, [inboundOrder]);

  useEffect(() => {
    if (inboundOrder?.packages) {
      const maxPages = Math.ceil(inboundOrder.packages.length / paginationState.itemsPerPage);
      if (paginationState.currentPage > maxPages && maxPages > 0) {
        setPaginationState(prev => ({
          ...prev,
          currentPage: maxPages,
        }));
      }
    }
  }, [inboundOrder?.packages, paginationState.itemsPerPage]);

  const navigate = useNavigate();

  const title = success ? `${t('inbound.inbound')} / ${t('inbound.create_cta')}` : t('inbound.inbound');
  const headline = success ? t('inbound.create_headline') : t('inbound.inbound');

  return (
    !isLoading && (
      <OverlayNavProvider entityIds={navIds} basePath="/warehouse/inbound">
        <InboundCreateShell
          step={success ? 3 : undefined}
          title={title}
          headline={headline}
          orderId={id}
          status={inboundOrder?.inbound_state}
          headerActions={<OverlayNavArrows />}>
          <div className="flex flex-col gap-y-20">
            {success && (
              <WarningBox
                colorType={WarningBoxColorType.Successful}
                title={t('inbound.success_title')}
                copy={t('inbound.success_copy')}
              />
            )}
            {inboundOrder && inboundOrder.inbound_state?.key !== Status.CANCEL && (
              <DownloadSection
                loading={downloadLoading}
                title={t('inbound.delivery_slip_download_title')}
                copy={t('inbound.delivery_slip_download_copy')}
                onDownloadClick={async () => {
                  const fileName = `order_${id}_delivery_slip.pdf`;
                  setDownloadLoading(true);
                  let blob;
                  try {
                    blob = await getInboundDeliverySlip(id);
                  } catch (e) {
                    setDownloadLoading(false);
                  }
                  if (!blob) {
                    setDownloadLoading(false);
                    throw new Error('Failed to download delivery slip');
                  }
                  const link = document.createElement('a');
                  link.href = URL.createObjectURL(blob);
                  link.download = fileName;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  setDownloadLoading(false);
                }}
              />
            )}

            <Section className="flex flex-col gap-y-32">
              <InboundConfiguratorForm
                readOnly
                onPaginationChange={handlePaginationChange}
                currentPage={paginationState.currentPage}
                itemsPerPage={paginationState.itemsPerPage}
                totalPages={Math.ceil(Number(inboundOrder?.total_pages) / paginationState.itemsPerPage)}
                inboundOrder={inboundOrder}
              />
              {inboundOrder && <InboundBackordersCard inboundOrder={inboundOrder} />}
              <Divider className="" />
              <ActionBar>
                <Button
                  title={t('inbound.to_overview')}
                  onClick={() => {
                    navigate('/warehouse/inbound');
                  }}
                  tone={ButtonToneProps.PRIMARY_BLUE}
                  // disabled={submitDisabled}
                />
              </ActionBar>
            </Section>
          </div>
        </InboundCreateShell>
      </OverlayNavProvider>
    )
  );
};

export default InboundCreateConfirm;
